import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { Building2, User, Save, Loader2, Upload, Image, FileText, Bell, AlertTriangle, Trash2, Eye, EyeOff, Lock, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const Settings = () => {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [closingAccount, setClosingAccount] = useState(false);
  const [closePassword, setClosePassword] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [showClosePassword, setShowClosePassword] = useState(false);
  
  // Profile editing state
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '' });
  const [passwordForm, setPasswordForm] = useState({ 
    current_password: '', 
    new_password: '', 
    confirm_password: '' 
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  
  const [settings, setSettings] = useState({
    name: '',
    logo: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    tax_id: '',
    pdf_header_text: '',
    pdf_footer_text: '',
    notification_email: true,
    notification_push: true
  });

  useEffect(() => {
    fetchSettings();
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/auth/me');
      setUserProfile(res.data);
      setProfileForm({ name: res.data.name || '', phone: res.data.phone || '' });
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await api.get('/company/settings');
      setSettings({
        name: res.data.name || '',
        logo: res.data.logo || '',
        address: res.data.address || '',
        phone: res.data.phone || '',
        email: res.data.email || '',
        website: res.data.website || '',
        tax_id: res.data.tax_id || '',
        pdf_header_text: res.data.pdf_header_text || '',
        pdf_footer_text: res.data.pdf_footer_text || '',
        notification_email: res.data.notification_email ?? true,
        notification_push: res.data.notification_push ?? true
      });
    } catch (error) {
      toast.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/company/settings', settings);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande (máximo 5MB)');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/company/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, logo: res.data.logo }));
      toast.success('Logo actualizado');
    } catch (error) {
      toast.error('Error al subir el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCloseAccount = async () => {
    if (!closePassword) {
      toast.error('Debes introducir tu contraseña');
      return;
    }

    setClosingAccount(true);
    try {
      await api.post('/company/close-account', {
        password: closePassword,
        reason: closeReason
      });
      toast.success('Cuenta cerrada correctamente');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cerrar la cuenta'));
    } finally {
      setClosingAccount(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    
    setSavingProfile(true);
    try {
      const res = await api.put('/auth/profile', {
        name: profileForm.name.trim(),
        phone: profileForm.phone.trim() || null
      });
      setUserProfile(res.data);
      setProfileDialogOpen(false);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar el perfil'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Completa todos los campos');
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setSavingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      setPasswordDialogOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Contraseña actualizada correctamente');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cambiar la contraseña'));
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      <div className="space-y-6 max-w-4xl" data-testid="settings-page">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Configuración</h1>
          <p className="text-slate-500">Personaliza tu empresa y los partes de trabajo</p>
        </div>

        <Tabs defaultValue="company" className="space-y-6">
          <TabsList>
            <TabsTrigger value="company">Empresa</TabsTrigger>
            <TabsTrigger value="branding">Logo y Marca</TabsTrigger>
            <TabsTrigger value="pdf">PDFs</TabsTrigger>
            <TabsTrigger value="notifications">Notificaciones</TabsTrigger>
            <TabsTrigger value="account" className="text-red-600">Cuenta</TabsTrigger>
          </TabsList>

          {/* Company Tab */}
          <TabsContent value="company" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Información de la empresa
                </CardTitle>
                <CardDescription>Datos que aparecerán en los partes de trabajo</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre de la empresa</Label>
                    <Input
                      value={settings.name}
                      onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                      data-testid="company-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>NIF/CIF</Label>
                    <Input
                      value={settings.tax_id}
                      onChange={(e) => setSettings({ ...settings, tax_id: e.target.value })}
                      placeholder="B12345678"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Dirección</Label>
                  <Input
                    value={settings.address}
                    onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                    placeholder="Calle, número, ciudad, código postal"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input
                      value={settings.phone}
                      onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                      placeholder="+34 600 000 000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email de contacto</Label>
                    <Input
                      type="email"
                      value={settings.email}
                      onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                      placeholder="info@empresa.com"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Sitio web</Label>
                  <Input
                    value={settings.website}
                    onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                    placeholder="https://www.empresa.com"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Mi cuenta
                </CardTitle>
                <CardDescription>Gestiona tu información personal y seguridad</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <div className="p-3 bg-slate-50 rounded-lg">{userProfile?.name || user?.name}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <div className="p-3 bg-slate-50 rounded-lg">{userProfile?.email || user?.email}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <div className="p-3 bg-slate-50 rounded-lg">{userProfile?.phone || 'No especificado'}</div>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-2">
                  {/* Edit Profile Dialog */}
                  <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" data-testid="edit-profile-btn">
                        <Pencil className="w-4 h-4 mr-2" />
                        Editar perfil
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Editar perfil</DialogTitle>
                        <DialogDescription>Actualiza tu información personal</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Nombre *</Label>
                          <Input
                            value={profileForm.name}
                            onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                            placeholder="Tu nombre completo"
                            data-testid="profile-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Teléfono</Label>
                          <Input
                            value={profileForm.phone}
                            onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                            placeholder="+34 600 000 000"
                            data-testid="profile-phone-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <div className="p-3 bg-slate-100 rounded-lg text-slate-500 text-sm">
                            {userProfile?.email || user?.email}
                            <span className="block text-xs mt-1">El email no se puede cambiar</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setProfileDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button onClick={handleSaveProfile} disabled={savingProfile} data-testid="save-profile-btn">
                          {savingProfile ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Guardando...
                            </>
                          ) : (
                            'Guardar cambios'
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Change Password Dialog */}
                  <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" data-testid="change-password-btn">
                        <Lock className="w-4 h-4 mr-2" />
                        Cambiar contraseña
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Cambiar contraseña</DialogTitle>
                        <DialogDescription>Introduce tu contraseña actual y la nueva</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Contraseña actual *</Label>
                          <div className="relative">
                            <Input
                              type={showCurrentPassword ? 'text' : 'password'}
                              value={passwordForm.current_password}
                              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                              placeholder="Tu contraseña actual"
                              data-testid="current-password-input"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                            >
                              {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Nueva contraseña *</Label>
                          <div className="relative">
                            <Input
                              type={showNewPassword ? 'text' : 'password'}
                              value={passwordForm.new_password}
                              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                              placeholder="Mínimo 6 caracteres"
                              data-testid="new-password-input"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                            >
                              {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Confirmar nueva contraseña *</Label>
                          <Input
                            type="password"
                            value={passwordForm.confirm_password}
                            onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                            placeholder="Repite la nueva contraseña"
                            data-testid="confirm-password-input"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button onClick={handleChangePassword} disabled={savingPassword} data-testid="save-password-btn">
                          {savingPassword ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Cambiando...
                            </>
                          ) : (
                            'Cambiar contraseña'
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Logo de la empresa
                </CardTitle>
                <CardDescription>Tu logo aparecerá en todos los partes de trabajo PDF</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-6">
                  <div className="w-32 h-32 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden">
                    {settings.logo ? (
                      <img src={settings.logo} alt="Logo" className="w-full h-full object-contain" />
                    ) : (
                      <Image className="w-10 h-10 text-slate-300" />
                    )}
                  </div>
                  <div className="flex-1 space-y-3">
                    <p className="text-sm text-slate-500">
                      Sube tu logo en formato PNG, JPG o SVG. Tamaño recomendado: 200x200px. Máximo 5MB.
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      data-testid="upload-logo-btn"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {settings.logo ? 'Cambiar logo' : 'Subir logo'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PDF Tab */}
          <TabsContent value="pdf" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Personalización de PDFs
                </CardTitle>
                <CardDescription>Configura el texto que aparecerá en todos los partes de trabajo</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Texto de cabecera (opcional)</Label>
                  <Textarea
                    value={settings.pdf_header_text}
                    onChange={(e) => setSettings({ ...settings, pdf_header_text: e.target.value })}
                    placeholder="Texto que aparecerá debajo del logo en todos los PDFs..."
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Texto de pie de página (opcional)</Label>
                  <Textarea
                    value={settings.pdf_footer_text}
                    onChange={(e) => setSettings({ ...settings, pdf_footer_text: e.target.value })}
                    placeholder="Ej: Gracias por confiar en nosotros. www.empresa.com"
                    rows={2}
                  />
                </div>
                <div className="p-4 bg-sky-50 rounded-lg">
                  <p className="text-sm text-sky-800">
                    <strong>Tip:</strong> Puedes crear plantillas personalizadas con diferentes diseños en la sección de "Plantillas" del menú.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Preferencias de notificaciones
                </CardTitle>
                <CardDescription>Configura cómo quieres recibir las alertas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Notificaciones por email</p>
                    <p className="text-sm text-slate-500">Recibe alertas de urgencias y resúmenes por correo</p>
                  </div>
                  <Switch
                    checked={settings.notification_email}
                    onCheckedChange={(checked) => setSettings({ ...settings, notification_email: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Notificaciones push</p>
                    <p className="text-sm text-slate-500">Recibe alertas instantáneas en el navegador</p>
                  </div>
                  <Switch
                    checked={settings.notification_push}
                    onCheckedChange={(checked) => setSettings({ ...settings, notification_push: checked })}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Account Tab - Danger Zone */}
          <TabsContent value="account" className="space-y-6">
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Zona de peligro
                </CardTitle>
                <CardDescription>Acciones irreversibles sobre tu cuenta</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-6 border border-red-200 rounded-lg bg-red-50/50">
                  <h3 className="font-semibold text-red-900 mb-2">Eliminar cuenta permanentemente</h3>
                  <p className="text-sm text-red-700 mb-4">
                    Al eliminar tu cuenta:
                  </p>
                  <ul className="text-sm text-red-700 mb-4 space-y-1 list-disc list-inside">
                    <li>Se cancelará tu suscripción de Stripe</li>
                    <li>Tus trabajadores perderán acceso inmediatamente</li>
                    <li><strong className="text-red-900">TODOS tus datos serán eliminados permanentemente</strong></li>
                    <li>Clientes, trabajos, reportes, plantillas - todo será borrado</li>
                    <li>Si deseas volver, deberás registrarte desde cero</li>
                  </ul>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" className="mt-2">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar mi cuenta
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-red-600">⚠️ ¿Eliminar cuenta permanentemente?</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                          <span className="block font-semibold text-red-600">Esta acción NO se puede deshacer.</span>
                          <span className="block">Se eliminarán permanentemente todos tus datos: clientes, trabajos, reportes, trabajadores y configuración.</span>
                          <span className="block">Si quieres volver a usar Aqua Nexia, tendrás que registrarte de nuevo.</span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Confirma tu contraseña *</Label>
                          <div className="relative">
                            <Input
                              type={showClosePassword ? 'text' : 'password'}
                              value={closePassword}
                              onChange={(e) => setClosePassword(e.target.value)}
                              placeholder="Tu contraseña actual"
                            />
                            <button
                              type="button"
                              onClick={() => setShowClosePassword(!showClosePassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                            >
                              {showClosePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>¿Por qué te vas? (opcional)</Label>
                          <Textarea
                            value={closeReason}
                            onChange={(e) => setCloseReason(e.target.value)}
                            placeholder="Tu feedback nos ayuda a mejorar..."
                            rows={2}
                          />
                        </div>
                      </div>
                      
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <Button
                          variant="destructive"
                          onClick={handleCloseAccount}
                          disabled={closingAccount || !closePassword}
                        >
                          {closingAccount ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Eliminando...
                            </>
                          ) : (
                            'Sí, ELIMINAR mi cuenta permanentemente'
                          )}
                        </Button>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Guardar cambios
              </>
            )}
          </Button>
        </div>
      </div>
    </OwnerLayout>
  );
};

export default Settings;
